import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTiktok, FaYoutube } from 'react-icons/fa';
import { SiX } from 'react-icons/si';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth, LinkedAccount } from '../contexts/AuthContext';
import './SocialAccounts.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type Platform = 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'twitter' | 'linkedin';
type BrandIconComponent = (props?: { className?: string }) => JSX.Element;

interface PlatformDef {
  id: Platform;
  name: string;
  Icon: BrandIconComponent;
  multiAccount: boolean;
}

const PLATFORMS: PlatformDef[] = [
  { id: 'youtube', name: 'YouTube Shorts', Icon: FaYoutube as unknown as BrandIconComponent, multiAccount: true },
  { id: 'instagram', name: 'Instagram Reels', Icon: FaInstagram as unknown as BrandIconComponent, multiAccount: false },
  { id: 'tiktok', name: 'TikTok', Icon: FaTiktok as unknown as BrandIconComponent, multiAccount: false },
  { id: 'facebook', name: 'Facebook Reels', Icon: FaFacebookF as unknown as BrandIconComponent, multiAccount: false },
  { id: 'twitter', name: 'X (Twitter)', Icon: SiX as unknown as BrandIconComponent, multiAccount: false },
  { id: 'linkedin', name: 'LinkedIn', Icon: FaLinkedinIn as unknown as BrandIconComponent, multiAccount: false },
];

type StatusChip = 'connected' | 'needs_reconnect' | 'not_connected';

function getPlatformStatus(accounts: LinkedAccount[]): StatusChip {
  if (accounts.length === 0) return 'not_connected';
  if (accounts.some(a => a.status === 'connected')) return 'connected';
  return 'needs_reconnect';
}

function normalizeProvider(provider: string): string {
  if (provider === 'google') return 'youtube';
  if (provider === 'x') return 'twitter';
  return provider;
}

function normalizeEmailForMatch(email: string | null | undefined): string {
  const raw = (email || '').trim().toLowerCase();
  if (!raw.includes('@')) return raw;
  const [localPart, domainPart] = raw.split('@');
  const domain = domainPart === 'googlemail.com' ? 'gmail.com' : domainPart;
  if (domain !== 'gmail.com') return `${localPart}@${domain}`;
  const localNoPlus = localPart.split('+')[0];
  const localNoDots = localNoPlus.replace(/\./g, '');
  return `${localNoDots}@gmail.com`;
}

const STATUS_LABELS: Record<StatusChip, string> = {
  connected: 'Connected',
  needs_reconnect: 'Reconnect',
  not_connected: 'Not Connected',
};

// ── Right panel states ────────────────────────────────────────

interface RightPanelProps {
  platform: PlatformDef;
  accounts: LinkedAccount[];
  selectedAccountId: string | null;
  onSelectAccount: (id: string) => void;
  onRefresh: () => Promise<void>;
  userEmail: string;
}

const Avatar: React.FC<{ src?: string | null; name?: string | null; size?: number }> = ({ src, name, size = 40 }) => {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  const initials = (name || '?').charAt(0).toUpperCase();

  let sizeClass = 'sa-avatar--md';
  if (size <= 36) sizeClass = 'sa-avatar--sm';
  if (size >= 52) sizeClass = 'sa-avatar--lg';

  if (src && !imageFailed) {
    return (
      <img
        src={src}
        alt={name || ''}
        className={`sa-avatar ${sizeClass}`}
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div className={`sa-avatar sa-avatar-fallback ${sizeClass}`}>
      {initials}
    </div>
  );
};

const PlatformBrandIcon: React.FC<{ platform: PlatformDef; large?: boolean }> = ({ platform, large }) => {
  const Icon = platform.Icon;
  return (
    <span
      className={[
        'sa-brand-icon',
        `sa-brand-icon--${platform.id}`,
        large ? 'sa-brand-icon--lg' : '',
      ].join(' ').trim()}
      aria-hidden="true"
    >
      <Icon />
    </span>
  );
};

// ── Tenant invite (email + copy link) ─────────────────────────

const INVITE_ERROR_MESSAGES: Record<string, string> = {
  tenant_linking_disabled: 'Tenant linking is not enabled. Ask an admin to turn it on for YouTube.',
  platform_not_allowed: 'YouTube linking is not allowed right now.',
  tenant_limit_reached: 'You have reached your tenant channel limit.',
};

const TenantInvitePanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [requireLogin, setRequireLogin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState<{ inviteUrl: string; emailSent: boolean; mailerConfigured: boolean; expiresAt: string } | null>(null);

  const createInvite = async (sendEmail: boolean) => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const res = await fetch(`${API_BASE}/api/me/link-invites`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: 'youtube', requireLogin, ...(sendEmail ? { email: email.trim() } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(INVITE_ERROR_MESSAGES[data.error] || data.error || 'Could not create invite');
      } else {
        setResult(data);
      }
    } catch {
      setError('Network error');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.inviteUrl);
      setCopied(true);
    } catch {
      setError('Could not copy — select the link and copy it manually.');
    }
  };

  return (
    <div className="sa-invite-panel">
      <div className="sa-invite-head">
        <div className="sa-multi-account-title">Invite a tenant</div>
        <button type="button" className="sa-btn-secondary" onClick={onClose}>Close</button>
      </div>
      <div className="sa-multi-account-text">
        The channel owner opens the invite, signs in with Google and consents. The channel then appears in your list.
      </div>
      <input
        className="sa-invite-input"
        type="email"
        placeholder="Tenant email (only needed to send by email)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <label className="sa-invite-check">
        <input type="checkbox" checked={requireLogin} onChange={(e) => setRequireLogin(e.target.checked)} />
        <span>Tenant must log in to VimAPI</span>
      </label>
      <div className="sa-invite-actions">
        <button
          type="button"
          className="sa-btn-primary"
          disabled={busy || !email.trim()}
          onClick={() => createInvite(true)}
        >
          Email invite
        </button>
        <button type="button" className="sa-btn-secondary" disabled={busy} onClick={() => createInvite(false)}>
          Create link
        </button>
      </div>
      {error && <div className="sa-error-banner">{error}</div>}
      {result && (
        <div className="sa-invite-result">
          {result.emailSent
            ? <div className="sa-multi-account-text">Email sent.</div>
            : email.trim() && !result.mailerConfigured
              ? <div className="sa-multi-account-text">Email is not configured on the server — share the link below instead.</div>
              : null}
          <div className="sa-invite-link-row">
            <input className="sa-invite-input" readOnly value={result.inviteUrl} onFocus={(e) => e.target.select()} />
            <button type="button" className="sa-btn-secondary" onClick={copyLink}>{copied ? 'Copied' : 'Copy link'}</button>
          </div>
          <div className="sa-multi-account-text">
            Single use. Expires {new Date(result.expiresAt).toLocaleString()}.
          </div>
        </div>
      )}
    </div>
  );
};

const RightPanel: React.FC<RightPanelProps> = ({
  platform, accounts, selectedAccountId, onSelectAccount, onRefresh, userEmail,
}) => {
  const [unlinkTarget, setUnlinkTarget] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const accountCardRef = useRef<HTMLDivElement | null>(null);

  const connected = accounts.filter(a => a.status === 'connected' || a.status === 'syncing');
  const needsReconnect = accounts.filter(a => a.status === 'needs_reconnect');
  const normalizedUserEmail = normalizeEmailForMatch(userEmail);
  const currentLoginLinkedByEmail = normalizedUserEmail
    ? accounts.find(a => normalizeEmailForMatch(a.providerEmail) === normalizedUserEmail) || null
    : null;
  const inferredCurrentLoginLinkedAccount = !currentLoginLinkedByEmail
    && accounts.length === 1
    && connected.length === 1
    && !accounts[0].providerEmail
      ? accounts[0]
      : null;
  const hasConnectedAccountWithUnknownEmail = !currentLoginLinkedByEmail
    && connected.some(a => !a.providerEmail);
  const currentLoginLinkedAccount = currentLoginLinkedByEmail || inferredCurrentLoginLinkedAccount;
  const loginIdentityState: 'linked' | 'unknown' | 'not_linked' = currentLoginLinkedAccount
    ? 'linked'
    : hasConnectedAccountWithUnknownEmail
      ? 'unknown'
      : 'not_linked';
  const selectedAccount = accounts.find(a => a.id === selectedAccountId)
    || currentLoginLinkedAccount
    || connected[0]
    || accounts[0]
    || null;

  const selectAccountAndReveal = (id: string) => {
    onSelectAccount(id);
    window.requestAnimationFrame(() => {
      accountCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Auto-select first connected account
  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      onSelectAccount(currentLoginLinkedAccount?.id || connected[0]?.id || accounts[0].id);
    }
  }, [accounts, selectedAccountId, currentLoginLinkedAccount, connected, onSelectAccount]);

  const handleConnect = () => {
    window.location.href = `${API_BASE}/auth/${platform.id}`;
  };

  const handleUnlink = async (accountId: string) => {
    setUnlinking(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/me/linked-accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Unlink failed');
      } else {
        await onRefresh();
        setUnlinkTarget(null);
      }
    } catch {
      setError('Network error');
    } finally {
      setUnlinking(false);
    }
  };

  const handleRefreshToken = async (accountId: string) => {
    setRefreshing(accountId);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/me/linked-accounts/${accountId}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) {
        setError(d.error || 'Refresh failed');
      } else {
        await onRefresh();
      }
    } catch {
      setError('Network error');
    } finally {
      setRefreshing(null);
    }
  };

  const handleSyncInsights = async (accountId: string) => {
    setSyncingId(accountId);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/accounts/${accountId}/insights/sync`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Sync failed');
      } else {
        await onRefresh();
      }
    } catch {
      setError('Network error');
    } finally {
      setSyncingId(null);
    }
  };

  // ── State A: not connected ──────────────────────────────────

  if (accounts.length === 0) {
    // Non-YouTube: Coming Soon
    if (platform.id !== 'youtube') {
      return (
        <div className="sa-right-panel">
          <div className="sa-state-a sa-state-a--center">
            <PlatformBrandIcon platform={platform} large />
            <div className="sa-state-a-title">{platform.name}</div>
            <div className="sa-state-a-subtitle">
              {platform.name} publishing drops soon.
              <br />YouTube is live now.
            </div>
            <div className="sa-coming-soon-badge">Coming Soon</div>
          </div>
        </div>
      );
    }

    // YouTube: identity separation explanation (spec §A)
    return (
      <div className="sa-right-panel">
        <div className="sa-state-a sa-state-a--left">
          <div className="sa-state-a-header">
            <PlatformBrandIcon platform={platform} />
            <div className="sa-state-a-title">Connect YouTube</div>
          </div>

          <div className="sa-identity-note">
            Signed in as <strong>{userEmail}</strong>. You can link this Google account or a different one for publishing.
          </div>

          {normalizedUserEmail && (
            <div className="sa-login-identity-card sa-login-identity-card--info" role="status" aria-live="polite">
              <div className="sa-login-identity-inline-main">
                <span className="sa-login-identity-info-icon" aria-hidden="true">i</span>
                <div className="sa-login-identity-inline-text">
                  Current VimAPI login <strong>{userEmail}</strong> • Not linked for YouTube yet. Choose this account in Google chooser to link it.
                </div>
              </div>
              <button className="sa-btn-secondary sa-login-identity-inline-btn" onClick={handleConnect}>
                Link account
              </button>
            </div>
          )}

          <div className="sa-perm-block">
            <div className="sa-perm-title">What we access</div>
            <ul className="sa-perm-list">
              <li>Upload videos and Shorts to YouTube</li>
              <li>Manage video metadata and thumbnails</li>
              <li>Read channel and video analytics</li>
            </ul>
          </div>

          <div className="sa-perm-block">
            <div className="sa-perm-title">What you get</div>
            <ul className="sa-perm-list">
              <li>One-click publish from VimAPI</li>
              <li>Fast per-post performance</li>
              <li>Channel health at a glance</li>
            </ul>
          </div>

          <div className="sa-secondary-note">
            ViralAI uses YouTube API Services. See our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>.
          </div>

          <button className="sa-btn-primary sa-btn-primary--spaced" onClick={handleConnect}>
            Link YouTube
          </button>
          <div className="sa-secondary-note">
            Login is done. This only grants YouTube publish access.
          </div>
          <button className="sa-btn-secondary" onClick={() => setShowInvite(v => !v)}>
            Invite a tenant channel
          </button>
          {showInvite && <TenantInvitePanel onClose={() => setShowInvite(false)} />}
        </div>
      </div>
    );
  }

  // ── State B / C: accounts exist ──────────────────────────────

  return (
    <div className="sa-right-panel">

      {/* Header */}
      <div className="sa-right-panel-header">
        <div className="sa-right-panel-heading">
          <PlatformBrandIcon platform={platform} />
          <div>
            <div className="sa-right-panel-title">{platform.name}</div>
            <div className="sa-right-panel-subtitle">
              {connected.length} live{needsReconnect.length > 0 ? `, ${needsReconnect.length} need attention` : ''}
            </div>
          </div>
        </div>
        {platform.multiAccount && (
          <button className="sa-btn-add-another" onClick={handleConnect}>
            + Add YouTube Account
          </button>
        )}
      </div>

      {error && <div className="sa-error-banner">{error}</div>}

      {platform.id === 'youtube' && normalizedUserEmail && (
        <div className="sa-login-identity-card sa-login-identity-card--info" role="status" aria-live="polite">
          <div className="sa-login-identity-inline-main">
            <span className="sa-login-identity-info-icon" aria-hidden="true">i</span>
            <div className="sa-login-identity-inline-text">
              Current VimAPI login <strong>{userEmail}</strong> • {loginIdentityState === 'linked'
                ? 'already linked for YouTube and prioritized by default.'
                : loginIdentityState === 'unknown'
                  ? 'may already be linked, but account email metadata is missing.'
                  : 'is not linked for YouTube yet.'}
            </div>
          </div>
          {loginIdentityState === 'linked' && currentLoginLinkedAccount ? (
            <button className="sa-btn-secondary sa-login-identity-inline-btn" onClick={() => onSelectAccount(currentLoginLinkedAccount.id)}>
              View this account
            </button>
          ) : loginIdentityState === 'unknown' ? (
            <button className="sa-btn-secondary sa-login-identity-inline-btn" onClick={() => onSelectAccount(connected[0]?.id || accounts[0].id)}>
              Review linked account
            </button>
          ) : (
            <button className="sa-btn-secondary sa-login-identity-inline-btn" onClick={handleConnect}>
              Link this or another account
            </button>
          )}
        </div>
      )}

      {platform.id === 'youtube' && platform.multiAccount && (
        <div className="sa-multi-account-callout">
          <div className="sa-multi-account-copy">
            <div className="sa-multi-account-title">Multi-account ready</div>
            <div className="sa-multi-account-text">
              You can add additional accounts for all the channels and creators you manage.
            </div>
          </div>
          <div className="sa-multi-account-actions">
            <button className="sa-btn-primary" onClick={handleConnect}>
              Add another channel
            </button>
            <button className="sa-btn-secondary" onClick={() => setShowInvite(v => !v)}>
              Invite a tenant
            </button>
          </div>
        </div>
      )}
      {showInvite && <TenantInvitePanel onClose={() => setShowInvite(false)} />}

      {/* ── State B: account list (always shown) ── */}
      <div className="sa-account-list">
        {accounts.map(acc => {
          const isSelected = selectedAccount?.id === acc.id;
          const displayName = acc.profileData?.displayName || acc.providerEmail || acc.providerId.slice(0, 12);
          const isCurrentLoginAccount = normalizedUserEmail
            && normalizeEmailForMatch(acc.providerEmail) === normalizedUserEmail;
          return (
            <button
              key={acc.id}
              className={`sa-account-list-row${isSelected ? ' sa-account-list-row--active' : ''}`}
              onClick={() => selectAccountAndReveal(acc.id)}
            >
              <Avatar src={acc.profileData?.avatarUrl} name={displayName} size={36} />
              <div className="sa-account-list-info">
                <div className="sa-account-list-name">{displayName}</div>
                {acc.providerEmail && acc.profileData?.displayName && (
                  <div
                    className="sa-account-list-email"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectAccountAndReveal(acc.id);
                    }}
                  >
                    {acc.providerEmail}
                  </div>
                )}
                {acc.linkRole === 'tenant' && (
                  <div className="sa-account-list-hint">Tenant channel</div>
                )}
                {isCurrentLoginAccount && (
                  <div className="sa-account-list-hint">Current login</div>
                )}
              </div>
              <StatusBadge
                status={acc.status}
                onReconnect={(e) => {
                  e.stopPropagation();
                  if (acc.linkRole === 'tenant') setShowInvite(true); else handleConnect();
                }}
              />
            </button>
          );
        })}
      </div>

      {/* ── State C: selected account detail ── */}
      {selectedAccount && (
        <>
          {/* Reconnect banner */}
          {selectedAccount.status === 'needs_reconnect' && (
            <div className="sa-reconnect-banner">
              <span>⚠️ Token expired — reconnect this account to continue publishing.</span>
              <button
                className="sa-reconnect-btn"
                onClick={() => handleRefreshToken(selectedAccount.id)}
                disabled={refreshing === selectedAccount.id}
              >
                {refreshing === selectedAccount.id ? 'Refreshing…' : 'Refresh Token'}
              </button>
              {selectedAccount.linkRole === 'tenant' ? (
                <button className="sa-reconnect-btn" onClick={() => setShowInvite(true)}>
                  Send new invite
                </button>
              ) : (
                <button className="sa-reconnect-btn" onClick={handleConnect}>
                  Reconnect via OAuth
                </button>
              )}
            </div>
          )}

          <div className="sa-account-card" ref={accountCardRef}>
            {/* Channel identity */}
            <div className="sa-account-card-header">
              <div className="sa-account-card-header-main">
                <Avatar
                  src={selectedAccount.profileData?.avatarUrl}
                  name={selectedAccount.profileData?.displayName || selectedAccount.providerEmail}
                  size={52}
                />
                <div>
                  <div className="sa-channel-name">
                    {selectedAccount.profileData?.displayName || selectedAccount.providerEmail || '—'}
                  </div>
                  {selectedAccount.profileData?.handle && (
                    <div className="sa-channel-handle">@{selectedAccount.profileData.handle}</div>
                  )}
                  {selectedAccount.providerEmail && (
                    <div className="sa-account-email">{selectedAccount.providerEmail}</div>
                  )}
                  {normalizedUserEmail && normalizeEmailForMatch(selectedAccount.providerEmail) === normalizedUserEmail && (
                    <div className="sa-current-login-note">Same account as VimAPI login</div>
                  )}
                  <div className="sa-account-meta">
                    Channel ID: <span className="sa-mono">{selectedAccount.profileData?.providerAccountId || selectedAccount.providerId}</span>
                  </div>
                </div>
              </div>
              <StatusBadge status={selectedAccount.status} large onReconnect={selectedAccount.linkRole === 'tenant' ? () => setShowInvite(true) : handleConnect} />
            </div>

            {/* Meta row */}
            <div className="sa-meta-row">
              <div className="sa-meta-item">
                <div className="sa-meta-label">Connected</div>
                <div className="sa-meta-value">{new Date(selectedAccount.connectedAt).toLocaleDateString()}</div>
              </div>
              {selectedAccount.status !== 'disconnected' && selectedAccount.tokenExpiresAt && (
                <div className="sa-meta-item">
                  <div className="sa-meta-label">Token expires</div>
                  <div className="sa-meta-value">{new Date(selectedAccount.tokenExpiresAt).toLocaleString()}</div>
                </div>
              )}
              {selectedAccount.status !== 'disconnected' && selectedAccount.lastInsightSyncAt && (
                <div className="sa-meta-item">
                  <div className="sa-meta-label">Last synced</div>
                  <div className="sa-meta-value">{new Date(selectedAccount.lastInsightSyncAt).toLocaleString()}</div>
                </div>
              )}
              {selectedAccount.profileData?.subscriberCount != null && (
                <div className="sa-meta-item">
                  <div className="sa-meta-label">Subscribers</div>
                  <div className="sa-meta-value">{selectedAccount.profileData.subscriberCount.toLocaleString()}</div>
                </div>
              )}
            </div>

            {selectedAccount.lastSyncError && (
              <div className="sa-sync-error">{selectedAccount.lastSyncError}</div>
            )}

            {/* Actions */}
            <div className="sa-account-actions">
              <button
                className="sa-btn-sync"
                onClick={() => handleSyncInsights(selectedAccount.id)}
                disabled={syncingId === selectedAccount.id || (selectedAccount.status !== 'connected' && selectedAccount.status !== 'syncing')}
              >
                {syncingId === selectedAccount.id ? 'Syncing…' : '↻ Refresh Insights'}
              </button>

              {selectedAccount.status === 'disconnected' ? (
                <button
                  className="sa-btn-secondary"
                  onClick={selectedAccount.linkRole === 'tenant' ? () => setShowInvite(true) : handleConnect}
                >
                  {selectedAccount.linkRole === 'tenant' ? 'Send new invite' : 'Reconnect'}
                </button>
              ) : unlinkTarget === selectedAccount.id ? (
                <div className="sa-inline-confirm">
                  <span className="sa-inline-confirm-note">
                    Unlinking stops future publishing for this account. History is kept.
                  </span>
                  <button
                    className="sa-btn-confirm-unlink"
                    onClick={() => handleUnlink(selectedAccount.id)}
                    disabled={unlinking}
                  >
                    {unlinking ? 'Unlinking…' : 'Confirm Unlink'}
                  </button>
                  <button
                    className="sa-btn-cancel"
                    onClick={() => setUnlinkTarget(null)}
                    disabled={unlinking}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="sa-btn-unlink"
                  onClick={() => setUnlinkTarget(selectedAccount.id)}
                >
                  Unlink This Account
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Status badge ──────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string; large?: boolean; onReconnect?: (e: React.SyntheticEvent) => void }> = ({ status, large, onReconnect }) => {
  const chipStatus: StatusChip =
    status === 'connected' ? 'connected'
    : status === 'needs_reconnect' ? 'needs_reconnect'
    : 'not_connected';

  const isActionable = chipStatus === 'needs_reconnect' && !!onReconnect;

  return (
    <span
      className={[
        'sa-status-badge',
        `sa-status-badge--${chipStatus}`,
        large ? 'sa-status-badge--lg' : '',
        isActionable ? 'sa-status-badge--actionable' : '',
      ].join(' ').trim()}
      role={isActionable ? 'button' : undefined}
      tabIndex={isActionable ? 0 : undefined}
      title={isActionable ? 'Click to reconnect this account' : undefined}
      onClick={isActionable ? onReconnect : undefined}
      onKeyDown={isActionable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onReconnect!(e);
        }
      } : undefined}
    >
      {STATUS_LABELS[chipStatus]}
    </span>
  );
};

// ── Main page ─────────────────────────────────────────────────

const LINK_ERROR_MESSAGES: Record<string, string> = {
  platform_not_allowed: 'Linking this platform is not allowed right now.',
  platform_limit_reached: 'You have reached the maximum number of accounts for this platform.',
  account_limit_reached: 'You have reached your total linked account limit.',
  channel_linked_to_other_user: 'This channel is already linked to another user. Ask its owner to send you a tenant invite instead.',
  no_youtube_channel: 'This Google account has no YouTube channel. Create one, or pick the account/channel that has it.',
  link_failed: 'Linking failed. Please try again.',
};

const SocialAccounts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();

  const [accounts, setAccounts] = useState<LinkedAccount[]>([]);
  const [loading, setLoading] = useState(true);

  const activePlatformId = (searchParams.get('platform') || 'youtube') as Platform;
  const activeLinkedAccountId = searchParams.get('linkedAccountId');
  const linkErrorCode = searchParams.get('error');
  const linkError = linkErrorCode ? (LINK_ERROR_MESSAGES[linkErrorCode] || 'Something went wrong while linking.') : null;

  const setActivePlatform = (p: Platform) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('platform', p);
      next.delete('linkedAccountId');
      return next;
    });
  };

  const setActiveLinkedAccountId = (id: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('linkedAccountId', id);
      return next;
    });
  };

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/accounts`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const normalizedAccounts: LinkedAccount[] = (data.accounts || []).map((account: LinkedAccount) => ({
          ...account,
          provider: normalizeProvider(account.provider),
        }));
        setAccounts(normalizedAccounts);
      }
    } catch {
      // leave stale
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Runs once on mount to handle redirect back from OAuth linking (?linkedAccountId=...)
  useEffect(() => {
    if (activeLinkedAccountId) {
      fetchAccounts().then(() => refreshUser());
    }
  }, []); // mount-only: intentional

  const handleRefresh = useCallback(async () => {
    await fetchAccounts();
    await refreshUser();
  }, [fetchAccounts, refreshUser]);

  const activePlatform = PLATFORMS.find(p => p.id === activePlatformId) || PLATFORMS[0];
  const platformAccounts = accounts.filter(a => a.provider === activePlatformId);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="page-header">
          <div>
            <h1>Social Accounts</h1>
            <p className="page-subtitle">Manage connected accounts and publish across platforms</p>
          </div>
        </div>

        {linkError && (
          <div className="sa-error-banner" role="alert">
            {linkError}{' '}
            <button
              type="button"
              className="sa-btn-secondary"
              onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('error'); return n; }, { replace: true })}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="sa-split-layout">
          {/* ── Left: platform list ── */}
          <nav className="sa-left-panel">
            {loading ? (
              <div className="sa-loading-msg">Loading…</div>
            ) : (
              PLATFORMS.map(p => {
                const pAccounts = accounts.filter(a => a.provider === p.id);
                const connectedCount = pAccounts.filter(a => a.status === 'connected' || a.status === 'syncing').length;
                const isActive = activePlatformId === p.id;
                return (
                  <button
                    key={p.id}
                    className={`sa-platform-row${isActive ? ' sa-platform-row--active' : ''}`}
                    onClick={() => setActivePlatform(p.id)}
                    aria-label={`${p.name}: ${connectedCount} connected`}
                  >
                    <PlatformBrandIcon platform={p} />
                    <div className="sa-platform-row-info">
                      <div className="sa-platform-row-name">{p.name}</div>
                    </div>
                    <span
                      className={`sa-platform-row-count-badge${connectedCount > 0 ? ' sa-platform-row-count-badge--connected' : ' sa-platform-row-count-badge--empty'}`}
                    >
                      {connectedCount}
                    </span>
                  </button>
                );
              })
            )}
          </nav>

          {/* ── Right: detail panel ── */}
          <div className="sa-right-panel-wrap">
            <RightPanel
              platform={activePlatform}
              accounts={platformAccounts}
              selectedAccountId={activeLinkedAccountId}
              onSelectAccount={setActiveLinkedAccountId}
              onRefresh={handleRefresh}
              userEmail={user?.email || ''}
            />
          </div>
        </div>
      </main>
    </div>
  );
};


export default SocialAccounts;
